import React from "react";

import {
  Box,
  Typography,
  Stack,
  Button,
  TextField,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";

import useAPIKeys from "../../hooks/useAPIKeys";
import APIKeyList from "./APIKeyList";
import APIDocumentation from "./APIDocumentation";
import APIKeyCreatedDialog from "./APIKeyCreatedDialog";
import useEvents from "../../hooks/useEvents";

const API_KEY_EXPIRY_OPTIONS = [
  { value: "never", label: "Never expires", expiresInDays: null },
  { value: "30", label: "30 days", expiresInDays: 30 },
  { value: "90", label: "90 days", expiresInDays: 90 },
  { value: "180", label: "180 days", expiresInDays: 180 },
  { value: "365", label: "1 year", expiresInDays: 365 },
];

function IntegrationPage({ organizationData }) {
  const {
    keys,
    addAPIKey,
    revokeAPIKey,
    deleteAPIKey,
    lastCreatedSecret,
    clearLastCreatedSecret,
  } = useAPIKeys();
  const events = useEvents();
  const [label, setLabel] = React.useState("Integration key");
  const [expiryChoice, setExpiryChoice] = React.useState("365");
  const [error, setError] = React.useState("");
  const [copyMessage, setCopyMessage] = React.useState("");
  const [creating, setCreating] = React.useState(false);

  const handleCopy = React.useCallback(async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyMessage("Copied to clipboard");
    } catch {
      setCopyMessage("Could not copy automatically");
    }
  }, []);

  React.useEffect(() => {
    if (!copyMessage) return undefined;
    const timer = window.setTimeout(() => setCopyMessage(""), 2500);
    return () => window.clearTimeout(timer);
  }, [copyMessage]);

  if (!organizationData) {
    return <Box>Loading...</Box>;
  }

  const activePreviewKey =
    lastCreatedSecret?.secret ||
    keys.find((k) => k.status !== "revoked" && k.isLegacy && k.key)?.key ||
    keys.find((k) => k.status !== "revoked" && !k.isLegacy)?.displayKey;

  const selectedExpiry = API_KEY_EXPIRY_OPTIONS.find(
    (option) => option.value === expiryChoice,
  );
  const expiresInDays = selectedExpiry?.expiresInDays ?? null;

  return (
    <Box>
      <Stack>
        <Typography variant="h4">API keys</Typography>

        <Typography style={{ fontSize: 20 }}>
          Create organization API keys for automation and AI agents. New keys
          use the <code>m45_</code> format, support expiration and revoke, and
          work with read-only client analytics endpoints. Legacy keys continue
          to work until you revoke or delete them.
        </Typography>

        {copyMessage ? (
          <Alert severity="success" sx={{ mt: 2 }}>
            {copyMessage}
          </Alert>
        ) : null}
        {error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : null}

        <Box style={{ marginTop: 12 }}>
          <Stack spacing={2}>
            <TextField
              label="Key label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              size="small"
              sx={{ maxWidth: 420 }}
            />
            <FormControl size="small" sx={{ maxWidth: 420 }}>
              <InputLabel id="api-key-expiry-label">Expiration</InputLabel>
              <Select
                labelId="api-key-expiry-label"
                label="Expiration"
                value={expiryChoice}
                onChange={(e) => setExpiryChoice(e.target.value)}
              >
                {API_KEY_EXPIRY_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <APIKeyList
              snapshot={keys}
              onCopy={handleCopy}
              onRevoke={async (keyId) => {
                setError("");
                try {
                  await revokeAPIKey(keyId);
                  events.logButtonClick({
                    buttonName: "Revoke API Key",
                    pageName: "Developers",
                    tabName: "Integration",
                  });
                } catch (e) {
                  setError(e.message || "Failed to revoke API key");
                }
              }}
              onDeleteLegacy={async (keyId) => {
                setError("");
                try {
                  await deleteAPIKey(keyId);
                  events.logButtonClick({
                    buttonName: "Delete API Key",
                    pageName: "Developers",
                    tabName: "Integration",
                  });
                } catch (e) {
                  setError(e.message || "Failed to delete legacy API key");
                }
              }}
            />
            <Button
              variant="contained"
              disabled={creating}
              onClick={async () => {
                setError("");
                setCreating(true);
                try {
                  await addAPIKey({
                    label: label.trim() || "API key",
                    expiresInDays,
                  });
                  events.logButtonClick({
                    buttonName: "Add API Key",
                    pageName: "Developers",
                    tabName: "Integration",
                  });
                } catch (e) {
                  setError(e.message || "Failed to create API key");
                } finally {
                  setCreating(false);
                }
              }}
              color="primary"
            >
              {creating ? "Creating…" : "Create API key"}
            </Button>
          </Stack>
        </Box>

        <APIDocumentation apiKeyPreview={activePreviewKey} />

        <APIKeyCreatedDialog
          open={Boolean(lastCreatedSecret)}
          secretData={lastCreatedSecret}
          onClose={clearLastCreatedSecret}
          onCopy={handleCopy}
        />
      </Stack>
    </Box>
  );
}

export default IntegrationPage;
